import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { PeopleSubnav } from '@/pages/people/people-subnav';
import { usePreferences } from '@/stores/preferences-store';

interface AnniversaryRow {
  id: string;
  firstName: string;
  lastName: string;
  anniversaryDate: string;
  years: number;
  photoUrl: string | null;
}

type AnniversaryKind = 'marriage' | 'membership';
type CelebrationRange = 'month' | 'today' | 'week';

const personName = (row: AnniversaryRow) => [row.firstName, row.lastName].filter(Boolean).join(' ');

const formatCelebrationDay = (iso: string, locale: string): string => {
  const parsed = DateTime.fromISO(iso, { zone: 'utc' }).setLocale(locale);
  if (!parsed.isValid) return '—';
  return parsed.toFormat('MMM d');
};

export const AnniversariesPage = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);

  const currentMonth = useMemo(
    () => DateTime.now().setZone(timezone).month,
    [timezone],
  );

  const [month, setMonth] = useState(String(currentMonth));
  const [kind, setKind] = useState<AnniversaryKind>('marriage');
  const [range, setRange] = useState<CelebrationRange>('month');
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ['people', 'anniversaries', month, kind, range, page],
    queryFn: () =>
      api.get<{ data: AnniversaryRow[]; page: { page: number; totalItems: number; totalPages: number } }>(
        '/people/anniversaries',
        {
          query: {
            month,
            kind,
            range,
            page,
            pageSize: 25,
          },
        },
      ),
  });

  const columns: DataTableColumn<AnniversaryRow>[] = [
    {
      id: 'date',
      header: t('anniversaries.columns.date'),
      width: '9rem',
      cell: (row) => formatCelebrationDay(row.anniversaryDate, locale),
    },
    {
      id: 'name',
      header: t('anniversaries.columns.name'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.id}
          name={personName(row)}
          photoUrl={row.photoUrl}
          showPhoto
          className="font-medium"
        />
      ),
    },
    {
      id: 'years',
      header: t('anniversaries.columns.years'),
      cell: (row) => t('anniversaries.yearsCount', { count: row.years }),
    },
  ];

  const rows = list.data?.data ?? [];
  const emptyMessage =
    range === 'today'
      ? t('anniversaries.emptyToday')
      : range === 'week'
        ? t('anniversaries.emptyWeek')
        : t('anniversaries.emptyMonth');

  return (
    <div>
      <PageHeader title={t('anniversaries.title')} description={t('anniversaries.description')} />
      <PeopleSubnav />

      <div className="mb-4 flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {(['marriage', 'membership'] as const).map((item) => (
            <Button
              key={item}
              size="sm"
              variant={kind === item ? 'primary' : 'outline'}
              onClick={() => {
                setKind(item);
                setPage(1);
              }}
            >
              {t(`anniversaries.filters.${item}`)}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <FormField label={t('anniversaries.month')} className="w-full sm:max-w-xs">
            {() => (
              <Listbox
                value={month}
                disabled={range !== 'month'}
                onChange={(value) => {
                  setMonth(value ?? String(currentMonth));
                  setRange('month');
                  setPage(1);
                }}
                options={Array.from({ length: 12 }, (_, index) => {
                  const value = String(index + 1);
                  return { value, label: t(`anniversaries.months.${value}`) };
                })}
              />
            )}
          </FormField>

          <div className="flex flex-wrap gap-2">
            <Button
              size="md"
              variant={range === 'today' ? 'primary' : 'outline'}
              onClick={() => {
                setRange((current) => (current === 'today' ? 'month' : 'today'));
                setPage(1);
              }}
            >
              {t('anniversaries.filters.today')}
            </Button>
            <Button
              size="md"
              variant={range === 'week' ? 'primary' : 'outline'}
              onClick={() => {
                setRange((current) => (current === 'week' ? 'month' : 'week'));
                setPage(1);
              }}
            >
              {t('anniversaries.filters.thisWeek')}
            </Button>
          </div>
        </div>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{emptyMessage}</p>}
      />

      {list.data?.page && list.data.page.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={list.data.page.page}
            pageSize={25}
            totalItems={list.data.page.totalItems}
            totalPages={list.data.page.totalPages}
            onPageChange={setPage}
            labels={{
              showing: ({ from, to, total }) => `${from}–${to} / ${total}`,
              perPage: tCommon('pagination.perPage', { defaultValue: 'Per page' }),
              first: tCommon('pagination.first', { defaultValue: 'First' }),
              previous: tCommon('pagination.previous', { defaultValue: 'Previous' }),
              next: tCommon('pagination.next', { defaultValue: 'Next' }),
              last: tCommon('pagination.last', { defaultValue: 'Last' }),
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
